import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Button, TextField, Grid, Card, CardContent, CardActions, Chip,
    Dialog, DialogTitle, DialogContent, DialogActions, Paper, Select, MenuItem, FormControl, InputLabel,
    InputAdornment, ToggleButton, ToggleButtonGroup, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Autocomplete, CircularProgress
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../../config';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import DescriptionIcon from '@mui/icons-material/Description';
import BusinessIcon from '@mui/icons-material/Business';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FlagIcon from '@mui/icons-material/Flag';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import HistoryIcon from '@mui/icons-material/History';
import Loading from '../../components/Loading';

function SoftwareLicenses() {
    const [licenses, setLicenses] = useState([]);
    const [users, setUsers] = useState([]);
    const [divisions, setDivisions] = useState([]);
    const [countries, setCountries] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [viewMode, setViewMode] = useState('grid');
    const [filter, setFilter] = useState('All');
    const [newLicense, setNewLicense] = useState({
        name: '', description: '', requested_by_id: '', requested_by_name: '', requested_by_email: '', department_id: '', country_id: '', supplier_id: '', status: 'Active'
    });
    const [search, setSearch] = useState('');
    
    // AD Search State
    const [adUsers, setAdUsers] = useState([]);
    const [adLoading, setAdLoading] = useState(false);

    const handleADSearch = async (query) => {
        if (!query || query.length < 3) {
            setAdUsers([]);
            return;
        }
        setAdLoading(true);
        try {
            const res = await axios.get(`${API_URL}/tickets/search-ad-users?q=${query}`, { headers });
            setAdUsers(res.data);
        } catch (e) {
            console.error(e);
        } finally {
            setAdLoading(false);
        }
    };

    const navigate = useNavigate();
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        fetchLicenses();
        fetchSetupData();
    }, []);

    const fetchSetupData = async () => {
        try {
            const [resUsers, resDivs, resCountries, resSuppliers] = await Promise.all([
                axios.get(`${API_URL}/setup/users`, { headers }),
                axios.get(`${API_URL}/setup/divisions`, { headers }),
                axios.get(`${API_URL}/setup/countries`, { headers }),
                axios.get(`${API_URL}/it-suppliers`, { headers })
            ]);
            setUsers(resUsers.data);
            setDivisions(resDivs.data);
            setCountries(resCountries.data);
            setSuppliers(resSuppliers.data);
        } catch (e) { console.error(e); }
    };

    const fetchLicenses = async () => {
        try {
            const res = await axios.get(`${API_URL}/software-licenses`, { headers });
            setLicenses(res.data);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    const handleCreate = async () => {
        try {
            await axios.post(`${API_URL}/software-licenses`, newLicense, { headers });
            setOpen(false);
            setNewLicense({ name: '', description: '', requested_by_id: '', requested_by_name: '', requested_by_email: '', department_id: '', country_id: '', supplier_id: '', status: 'Active' });
            fetchLicenses();
        } catch (e) { console.error(e); }
    };

    const filtered = licenses.filter(l =>
        (filter === 'All' || l.status === filter) &&
        (l.name.toLowerCase().includes(search.toLowerCase()) ||
            (l.description && l.description.toLowerCase().includes(search.toLowerCase())))
    );

    const getStatusColor = (status) => {
        switch (status) {
            case 'Active': return 'success';
            case 'Expired': return 'error';
            case 'Pending Renewal': return 'warning';
            default: return 'default';
        }
    };

    if (loading) return <Loading />;

    return (
        <Box p={{ xs: 2, md: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'center' }, mb: 3, gap: 2 }}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {['All', 'Active', 'Expired', 'Pending Renewal'].map(f => (
                        <Chip
                            key={f}
                            label={f}
                            onClick={() => setFilter(f)}
                            color={filter === f ? 'primary' : 'default'}
                            variant={filter === f ? 'filled' : 'outlined'}
                            sx={{ fontWeight: 'bold' }}
                        />
                    ))}
                </Box>

                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                    <TextField
                        placeholder="Search licenses..."
                        size="small"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                            sx: { borderRadius: 5, bgcolor: 'white' }
                        }}
                    />
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <ToggleButtonGroup
                            value={viewMode}
                            exclusive
                            onChange={(e, newMode) => { if (newMode) setViewMode(newMode); }}
                            size="small"
                            sx={{ bgcolor: 'white', borderRadius: 2 }}
                        >
                            <ToggleButton value="grid" aria-label="grid view">
                                <ViewModuleIcon />
                            </ToggleButton>
                            <ToggleButton value="list" aria-label="list view">
                                <ViewListIcon />
                            </ToggleButton>
                        </ToggleButtonGroup>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setOpen(true)}
                            sx={{ borderRadius: 5, px: 3 }}
                        >
                            New License
                        </Button>
                    </Box>
                </Box>
            </Box>

            {viewMode === 'grid' ? (
                <Grid container spacing={3}>
                    {filtered.map(l => (
                        <Grid item xs={12} md={4} lg={3} key={l.id}>
                            <Card
                                sx={{ height: '100%', borderRadius: 3, boxShadow: 1, cursor: 'pointer', transition: '0.3s', '&:hover': { boxShadow: 4 } }}
                                onClick={() => navigate(`/software-licenses/${l.id}`)}
                            >
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Box sx={{ overflow: 'hidden', mr: 1 }}>
                                            <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.name}</Typography>
                                            {l.supplier_name && <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>{l.supplier_name}</Typography>}
                                        </Box>
                                        <Chip label={l.status} size="small" color={getStatusColor(l.status)} sx={{ height: 20, fontSize: '0.7rem', flexShrink: 0 }} />
                                    </Box>

                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                                        {l.latest_renewal && (
                                            <Box display="flex" flexDirection="column" bgcolor="rgba(0,0,0,0.03)" p={1} borderRadius={1}>
                                                <Box display="flex" alignItems="center">
                                                    <HistoryIcon fontSize="small" sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                                                    <Typography variant="caption" fontWeight="bold">Renewed: {new Date(l.latest_renewal).toLocaleDateString()}</Typography>
                                                </Box>
                                                {(l.latest_start_date || l.latest_end_date) && (
                                                    <Typography variant="caption" color="textSecondary" sx={{ ml: 3, display: 'block' }}>
                                                        {l.latest_start_date ? new Date(l.latest_start_date).toLocaleDateString() : '?'} - {l.latest_end_date ? new Date(l.latest_end_date).toLocaleDateString() : '?'}
                                                    </Typography>
                                                )}
                                            </Box>
                                        )}
                                        {l.department_name && (
                                            <Box display="flex" alignItems="center" color="text.secondary">
                                                <BusinessIcon fontSize="small" sx={{ mr: 1, fontSize: 16 }} />
                                                <Typography variant="caption">{l.department_name}</Typography>
                                            </Box>
                                        )}
                                        {l.country_name && (
                                            <Box display="flex" alignItems="center" color="text.secondary">
                                                <FlagIcon fontSize="small" sx={{ mr: 1, fontSize: 16 }} />
                                                <Typography variant="caption">{l.country_name}</Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                    {filtered.length === 0 && (
                        <Grid item xs={12}>
                            <Typography align="center" sx={{ py: 3, color: 'text.secondary' }}>No licenses found</Typography>
                        </Grid>
                    )}
                </Grid>
            ) : (
                <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 1 }}>
                    <Table>
                        <TableHead sx={{ bgcolor: '#f8fafc' }}>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Latest Renewal</TableCell>
                                <TableCell>Start Date</TableCell>
                                <TableCell>End Date</TableCell>
                                <TableCell>Supplier</TableCell>
                                <TableCell>Department</TableCell>
                                <TableCell>Country</TableCell>
                                <TableCell>Requested By</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filtered.map((l) => (
                                <TableRow key={l.id} hover onClick={() => navigate(`/software-licenses/${l.id}`)} sx={{ cursor: 'pointer' }}>
                                    <TableCell sx={{ fontWeight: 'bold' }}>{l.name}</TableCell>
                                    <TableCell>{l.latest_renewal ? new Date(l.latest_renewal).toLocaleDateString() : '-'}</TableCell>
                                    <TableCell>{l.latest_start_date ? new Date(l.latest_start_date).toLocaleDateString() : '-'}</TableCell>
                                    <TableCell>{l.latest_end_date ? new Date(l.latest_end_date).toLocaleDateString() : '-'}</TableCell>
                                    <TableCell>{l.supplier_name || '-'}</TableCell>
                                    <TableCell>{l.department_name || '-'}</TableCell>
                                    <TableCell>{l.country_name || '-'}</TableCell>
                                    <TableCell>{l.requested_by_name || '-'}</TableCell>
                                    <TableCell>
                                        <Chip label={l.status} size="small" color={getStatusColor(l.status)} />
                                    </TableCell>
                                    <TableCell>
                                        <Button size="small" endIcon={<ArrowForwardIcon />}>View</Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {filtered.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 3 }}>No licenses found</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Create Dialog */}
            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle>New Software License</DialogTitle>
                <DialogContent>
                    <Box display="flex" flexDirection="column" gap={2} mt={1}>
                        <TextField
                            label="App Name"
                            fullWidth
                            variant="outlined"
                            value={newLicense.name}
                            onChange={(e) => setNewLicense({ ...newLicense, name: e.target.value })}
                        />
                        <TextField
                            label="Description"
                            fullWidth
                            multiline
                            rows={2}
                            variant="outlined"
                            value={newLicense.description}
                            onChange={(e) => setNewLicense({ ...newLicense, description: e.target.value })}
                        />
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <Autocomplete
                                    freeSolo
                                    options={adUsers}
                                    loading={adLoading}
                                    getOptionLabel={(option) => {
                                        if (typeof option === 'string') return option;
                                        return option.displayName || option.name || '';
                                    }}
                                    onInputChange={(event, newInputValue) => {
                                        handleADSearch(newInputValue);
                                    }}
                                    filterOptions={(x) => x} 
                                    value={newLicense.requested_by_name ? { displayName: newLicense.requested_by_name, mail: newLicense.requested_by_email } : null}
                                    onChange={(e, newValue) => {
                                        if (typeof newValue === 'string') {
                                             setNewLicense({ 
                                                ...newLicense, 
                                                requested_by_name: newValue,
                                                requested_by_email: '',
                                                requested_by_id: null
                                            });
                                        } else if (newValue) {
                                            const name = newValue.displayName || newValue.username;
                                            const email = newValue.mail || newValue.email || newValue.userPrincipalName;
                                            
                                            setNewLicense({ 
                                                ...newLicense, 
                                                requested_by_name: name,
                                                requested_by_email: email,
                                                requested_by_id: null
                                            });
                                        } else {
                                            setNewLicense({ ...newLicense, requested_by_name: '', requested_by_email: '', requested_by_id: null });
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField 
                                            {...params} 
                                            label="Requested By" 
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <React.Fragment>
                                                        {adLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </React.Fragment>
                                                ),
                                            }}
                                        />
                                    )}
                                    renderOption={(props, option) => (
                                        <li {...props}>
                                            <Box>
                                                <Typography variant="body1">{option.displayName || option.name}</Typography>
                                                {(option.mail || option.userPrincipalName) && <Typography variant="caption" color="textSecondary">{option.mail || option.userPrincipalName}</Typography>}
                                            </Box>
                                        </li>
                                    )}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <Autocomplete
                                    options={divisions}
                                    getOptionLabel={(option) => option.name}
                                    value={divisions.find(d => d.id === newLicense.department_id) || null}
                                    onChange={(e, newValue) => setNewLicense({ ...newLicense, department_id: newValue ? newValue.id : null })}
                                    renderInput={(params) => <TextField {...params} label="Department" />}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <Autocomplete
                                    options={countries}
                                    getOptionLabel={(option) => option.name}
                                    value={countries.find(c => c.id === newLicense.country_id) || null}
                                    onChange={(e, newValue) => setNewLicense({ ...newLicense, country_id: newValue ? newValue.id : null })}
                                    renderInput={(params) => <TextField {...params} label="Country" />}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <Autocomplete
                                    options={suppliers}
                                    getOptionLabel={(option) => option.name}
                                    value={suppliers.find(s => s.id === newLicense.supplier_id) || null}
                                    onChange={(e, newValue) => setNewLicense({ ...newLicense, supplier_id: newValue ? newValue.id : null })}
                                    renderInput={(params) => <TextField {...params} label="Supplier" />}
                                />
                            </Grid>
                        </Grid>
                        <FormControl fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={newLicense.status}
                                label="Status"
                                onChange={(e) => setNewLicense({ ...newLicense, status: e.target.value })}
                            >
                                <MenuItem value="Active">Active</MenuItem>
                                <MenuItem value="Expired">Expired</MenuItem>
                                <MenuItem value="Pending Renewal">Pending Renewal</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button onClick={handleCreate} variant="contained" disabled={!newLicense.name}>Create License</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default SoftwareLicenses;
